<template>
    <div>
        <div class="flex flex-wrap flex-col gap-3">
            <BaseFileUpload
                v-slot="{ clickInput }"
                class="grow"
                :is-disabled="isDisabled || isUploading"
                is-multiple-file-upload-enabled
                @file-upload="(files) => handleFileUpload(files, false)">
                <button
                    class="w-full border-2 border-dashed border-valence-grey-300 rounded-lgr p-6 flex flex-col text-center items-center gap-2 hover:bg-gray-50"
                    @click="clickInput">
                    <i v-if="!isUploading" class="bi bi-cloud-upload text-2xl/none text-valence-primary-600" aria-hidden="true"></i>
                    <LoadingSpinner v-else class="h-7.5" />
                    <p class="body-small-semibold whitespace-nowrap">Drop files or click to browse</p>
                    <p class="text-xs text-valence-grey-400">DOCX, PDF up to 6MB; TXT, XLSX, Markdown up to 3MB each</p>
                </button>
            </BaseFileUpload>
            <FileAttachedTag v-for="file in additionalFiles" :key="file.id" :file="file" is-removable @remove="handleFileClear(file)" />
        </div>
        <BaseToast v-model:open="isErrorToastOpen" :description="errorMessage" />
    </div>
</template>

<script setup lang="ts">
import type { UploadedFile } from "~vue/appTypes";
import BaseFileUpload from "~vue/components/BaseFileUpload.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import FileAttachedTag from "~vue/components/FileAttachedTag.vue";
import { useFileUpload } from "~vue/composables/useFileUpload";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { formatOversizedFiles, type OversizedFile } from "~vue/utils/fileUpload";
import { computed, ref } from "vue";

interface PerformanceReviewUploadedFile extends UploadedFile {
    upload_type: string;
}

const { isDisabled = false, uploadType } = defineProps<{
    isDisabled?: boolean;
    uploadType: string;
}>();

const emit = defineEmits<{
    "document-uploaded": [documents: PerformanceReviewUploadedFile[], pasted: boolean];
    "document-removed": [document: PerformanceReviewUploadedFile];
    "document-selected": [document: { upload_type: string }];
    "document-upload-error": [document: { upload_type: string }, pasted: boolean];
}>();

const isUploading = ref(false);
const additionalFiles = ref<PerformanceReviewUploadedFile[]>([]);
const isErrorToastOpen = ref(false);
const errors = ref<string[]>([]);

const errorMessage = computed(() => {
    return errors.value.join("\n");
});

const { uploadFiles } = useFileUpload({
    unsupportedFileCallback: (files: File[]) => {
        errors.value.push(`The following files have unsupported formats: ${files.map((f) => f.name).join(", ")}. Please upload supported file types.`);
    },
    oversizedFileCallback: (files: OversizedFile[]) => {
        errors.value.push(`The following files exceed their size limits: ${formatOversizedFiles(files)}. Please upload smaller files.`);
    },
    errorCallback: (error: string) => {
        errors.value.push(error);
    },
});

const handleFileUpload = async (files: File[], pasted: boolean) => {
    errors.value = [];
    isUploading.value = true;
    emit("document-selected", { upload_type: uploadType });

    try {
        const data = await uploadFiles(files);
        // Ensure that the correct upload type is set so that when we link these files we know what type they are
        const dataWithUploadType = data.uploadedFiles.map((file: UploadedFile) => ({ ...file, upload_type: uploadType }));
        additionalFiles.value.push(...dataWithUploadType);
        emit("document-uploaded", dataWithUploadType, pasted);
    } catch (error) {
        errors.value.push(`${error instanceof Error ? error.message : String(error)}. Please try again.`);
    } finally {
        // Surface and report any failure: thrown errors as well as validation
        // failures (unsupported / oversized files) pushed via the upload callbacks.
        if (errors.value.length > 0) {
            isErrorToastOpen.value = true;
            emit("document-upload-error", { upload_type: uploadType }, pasted);
        }
        isUploading.value = false;
    }
};

const handleFileClear = (file: PerformanceReviewUploadedFile) => {
    additionalFiles.value = additionalFiles.value.filter((f) => f.id !== file.id);
    emit("document-removed", file);
};
</script>
